import React, { useEffect } from "react";
import { Card, Col, Container, Row } from "react-bootstrap";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Handshake, Home, ShieldCheck } from "lucide-react";
import ProjectContractFlow from "./tabs";
import SubcontractAgreementPage from "./SubcontractAgreement";
import { useBillingAccess } from "components/BillingAccessContext";
import { getAuthPayload, hasPermission, isContractorUser } from "Common/permissions";

const ContractTypeChooser: React.FC = () => {
  return (
    <div className="page-content contract-flow-page contract-type-chooser-page">
      <Container fluid>
        <style>{`
          .contract-type-shell { max-width: 1180px; margin: 0 auto; padding: clamp(22px, 4vh, 44px) 0 32px; }
          .contract-type-title { max-width: 720px; margin-inline: auto; }
          .contract-type-title h2 { font-size: clamp(28px, 3vw, 36px); line-height: 1.18; font-weight: 800; color: #111827; margin-bottom: 10px; }
          .contract-type-title p { font-size: 16px; color: #667085; }
          .contract-type-card-link { display: block; height: 100%; color: inherit; text-decoration: none; border-radius: 16px; }
          .contract-type-card { height: 100%; border: 1px solid #dce5f0; border-radius: 16px; background: #fff; box-shadow: 0 14px 35px rgba(15,23,42,.06); overflow: hidden; transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease; }
          .contract-type-card-link:hover .contract-type-card { transform: translateY(-3px); border-color: #8ba9fa; box-shadow: 0 20px 44px rgba(15,23,42,.11); }
          .contract-type-card-link:focus-visible { outline: 3px solid rgba(37, 99, 235, .5); outline-offset: 4px; }
          .contract-type-card-link:focus-visible .contract-type-card { border-color: #2563eb; box-shadow: 0 18px 38px rgba(37,99,235,.16); }
          .contract-type-card.project { border-color: #c4d5ff; }
          .contract-type-card.subcontract { border-color: #c9d8e8; }
          .contract-type-body { padding: clamp(24px, 3vw, 32px); text-align: center; display: flex; flex-direction: column; }
          .contract-type-icon { width: 96px; height: 96px; border-radius: 999px; display: inline-flex; align-items: center; justify-content: center; margin: 0 auto 20px; }
          .contract-type-icon.project { background: #eef4ff; color: #2457e6; }
          .contract-type-icon.subcontract { background: #eef6f7; color: #0f7f88; }
          .contract-type-card h4 { font-size: 21px; line-height: 1.3; font-weight: 800; color: #111827; margin-bottom: 18px; }
          .contract-type-divider { height: 1px; background: #e5e7eb; margin-bottom: 18px; }
          .contract-type-copy { min-height: 66px; max-width: 390px; margin: 0 auto 22px; color: #596579; font-size: 15px; line-height: 1.5; }
          .contract-type-list { display: grid; gap: 13px; text-align: left; margin: 0 0 26px; padding: 0; list-style: none; color: #475467; font-size: 14px; }
          .contract-type-list li { display: flex; align-items: flex-start; gap: 10px; line-height: 1.45; }
          .contract-type-list li svg { margin-top: 2px; color: #2563eb; }
          .contract-type-list svg { flex: 0 0 auto; }
          .contract-type-button { min-height: 52px; margin-top: auto; padding: 0 18px; border: 0; border-radius: 9px; color: #fff; background: linear-gradient(135deg, #2457e6 0%, #1d4ed8 100%); font-size: 14px; font-weight: 800; display: flex; align-items: center; justify-content: space-between; gap: 12px; box-shadow: 0 10px 22px rgba(37,87,230,.22); }
          .contract-type-card-link:hover .contract-type-button { background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%); }
          .contract-type-secure { width: min(680px, 100%); margin: 26px auto 0; border-top: 1px solid #dce5f0; padding: 20px 22px 0; display: flex; align-items: center; justify-content: center; gap: 14px; }
          .contract-type-secure-icon { width: 42px; height: 42px; margin: 0; border-radius: 999px; background: #eef4ff; color: #2457e6; display: inline-flex; align-items: center; justify-content: center; flex: 0 0 42px; line-height: 0; }
          .contract-type-secure-icon svg { display: block; width: 23px; height: 23px; margin: 0; }
          .contract-type-secure strong { display: block; color: #111827; line-height: 1.2; }
          .contract-type-secure > div > span { display: block; color: #596579; font-size: 13px; margin-top: 4px; }
          [data-bs-theme="dark"] .contract-type-chooser-page .contract-type-title p,
          [data-bs-theme="dark"] .contract-type-chooser-page .contract-type-copy,
          [data-bs-theme="dark"] .contract-type-chooser-page .contract-type-list,
          [data-bs-theme="dark"] .contract-type-chooser-page .contract-type-secure > div > span { color: #b8c4d3 !important; }
          [data-bs-theme="dark"] .contract-type-chooser-page .contract-type-secure { background: transparent !important; border: 0 !important; border-top: 1px solid #34445a !important; box-shadow: none !important; }
          [data-bs-theme="dark"] .contract-type-chooser-page .contract-type-card-link:hover .contract-type-card { border-color: #6f91ed !important; }
          @media (max-height: 820px) and (min-width: 768px) {
            .contract-type-shell { padding-top: 18px; }
            .contract-type-icon { width: 82px; height: 82px; margin-bottom: 16px; }
            .contract-type-icon svg { width: 48px; height: 48px; }
            .contract-type-body { padding-top: 24px; padding-bottom: 24px; }
            .contract-type-secure { margin-top: 20px; }
          }
          @media (max-width: 767.98px) {
            .contract-type-body { padding: 24px 20px; }
            .contract-type-secure { align-items: flex-start; justify-content: flex-start; }
          }
        `}</style>
        <div className="contract-type-shell">
          <div className="contract-type-title text-center mb-4">
            <h2>Choose what you want to create</h2>
            <p className="mb-0">Select the option that best matches the agreement you need.</p>
          </div>
          <Row className="g-4 justify-content-center">
            <Col lg={5} md={6}>
              <Link
                to="/create-contract?type=project"
                className="contract-type-card-link"
                aria-label="Create a project contract with a homeowner"
              >
                <Card className="contract-type-card project">
                  <Card.Body className="contract-type-body">
                    <span className="contract-type-icon project" aria-hidden="true">
                      <Home size={52} strokeWidth={1.8} />
                    </span>
                    <h4>Project Contract with Homeowner</h4>
                    <div className="contract-type-divider" />
                    <p className="contract-type-copy">
                      Choose this when starting a homeowner project with a main contract and payment milestones.
                    </p>
                    <ul className="contract-type-list">
                      <li><CheckCircle2 size={17} />Add the homeowner and project details</li>
                      <li><CheckCircle2 size={17} />Set scope, milestones, and payments</li>
                      <li><CheckCircle2 size={17} />Add related subcontract work later</li>
                    </ul>
                    <span className="contract-type-button project w-100">
                      <span>Start Project Contract</span><ArrowRight size={18} aria-hidden="true" />
                    </span>
                  </Card.Body>
                </Card>
              </Link>
            </Col>
            <Col lg={5} md={6}>
              <Link
                to="/create-contract?type=subcontract"
                className="contract-type-card-link"
                aria-label="Create a subcontractor agreement"
              >
                <Card className="contract-type-card subcontract">
                  <Card.Body className="contract-type-body">
                    <span className="contract-type-icon subcontract" aria-hidden="true">
                      <Handshake size={52} strokeWidth={1.8} />
                    </span>
                    <h4>Subcontractor Agreement</h4>
                    <div className="contract-type-divider" />
                    <p className="contract-type-copy">
                      Choose this for an agreement between a general contractor (GC) and a subcontractor.
                    </p>
                    <ul className="contract-type-list">
                      <li><CheckCircle2 size={17} />Add both businesses and signers</li>
                      <li><CheckCircle2 size={17} />Define scope, milestones, and payments</li>
                      <li><CheckCircle2 size={17} />Review and send for signature</li>
                    </ul>
                    <span className="contract-type-button subcontract w-100">
                      <span>Create Subcontractor Agreement</span><ArrowRight size={18} aria-hidden="true" />
                    </span>
                  </Card.Body>
                </Card>
              </Link>
            </Col>
          </Row>
          <div className="contract-type-secure">
            <span className="contract-type-secure-icon" aria-hidden="true"><ShieldCheck size={23} /></span>
            <div>
              <strong>Secure contract workflow</strong>
              <span>Your contracts and signature records are stored securely in one place.</span>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

const CreateContractRouter: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { state: billingAccess, openPaymentRecovery } = useBillingAccess();
  const type = searchParams.get("type");
  const hasLegacyDeepLink = Boolean(searchParams.get("id") || searchParams.get("tab") || searchParams.get("action"));
  const isNewContract = !searchParams.get("id");
  const isSubscriptionSuspended = billingAccess?.accessLevel === "suspended";
  const auth = getAuthPayload();
  const canCreateContract =
    auth.userType === "admin" ||
    (isContractorUser(auth.userType) && hasPermission("CREATE_CONTRACT"));

  useEffect(() => {
    if (canCreateContract && isSubscriptionSuspended && isNewContract) openPaymentRecovery();
  }, [canCreateContract, isNewContract, isSubscriptionSuspended, openPaymentRecovery]);

  if (isNewContract && !canCreateContract) {
    return <Navigate to="/access-denied" replace />;
  }

  if (isSubscriptionSuspended && isNewContract) {
    return <Navigate to="/get-contracts" replace />;
  }

  if (type === "subcontract") {
    return <SubcontractAgreementPage />;
  }

  if (type === "project" || hasLegacyDeepLink) {
    return <ProjectContractFlow />;
  }

  return <ContractTypeChooser />;
};

export default CreateContractRouter;
